/**
 * cart-delivery-progress.js
 *
 * Keeps the delivery fee progress bar in sync with the live cart.
 * Intercepts all Shopify cart API calls so the bar updates instantly
 * without a page reload whenever items are added, removed, or changed.
 *
 * Tier thresholds (SGD dollars):
 *   T1 = $20  → unlocks $6.90 fee
 *   T2 = $29  → unlocks $3.90 fee
 *   T3 = $45  → unlocks Free delivery
 *
 * Visual bar — three segments:
 *   Segment 1  $0  → $20  :  0% → 33%   (33 pp wide)
 *   Segment 2  $20 → $29  : 33% → 67%   (34 pp wide)
 *   Segment 3  $29 → $45  : 67% → 100%  (33 pp wide)
 *
 * Tick positions: 0%, 33%, 67%, 100%
 * Tick fills: every tier reached, including current, using segment >= (i+1)
 */

(function () {
  'use strict';

  // ── Thresholds (dollars) ──────────────────────────────────────
  var T1 = 20;
  var T2 = 29;
  var T3 = 45;

  // ── Format cents as a dollar string ──────────────────────────
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Derive bar state from cart total in cents ─────────────────
  function getState(totalCents) {
    var total = totalCents / 100;

    if (total < T1) {
      return {
        fill:    (total / T1) * 33,
        segment: 1,
        needed:  Math.round((T1 - total) * 100),
        freeMsg: false,
        done:    false
      };
    }

    if (total < T2) {
      return {
        fill:    33 + ((total - T1) / (T2 - T1)) * 34,
        segment: 2,
        needed:  Math.round((T2 - total) * 100),
        freeMsg: false,
        done:    false
      };
    }

    if (total < T3) {
      return {
        fill:    67 + ((total - T2) / (T3 - T2)) * 33,
        segment: 3,
        needed:  Math.round((T3 - total) * 100),
        freeMsg: true,
        done:    false
      };
    }

    return {
      fill:    100,
      segment: 4,
      needed:  0,
      freeMsg: true,
      done:    true
    };
  }

  // ── Apply state to the DOM ────────────────────────────────────
  function updateBar(totalCents, itemCount) {
    var wrapper = document.getElementById('CartDeliveryProgress');
    if (!wrapper) return;

    // Hide when cart is empty; show otherwise
    if (!itemCount || totalCents <= 0) {
      wrapper.style.display = 'none';
      return;
    }
    wrapper.style.display = '';

    var state   = getState(totalCents);
    var fillPct = Math.min(100, state.fill);

    // ── Message ───────────────────────────────────────────────
    var msgEl = wrapper.querySelector('.cdp-message');
    if (msgEl) {
      if (state.done) {
        msgEl.innerHTML = '\uD83C\uDF89 You\'ve unlocked <strong>free delivery!</strong>';
      } else if (state.freeMsg) {
        msgEl.innerHTML =
          'Add items worth <strong>' + formatMoney(state.needed) +
          '</strong> more for <strong>free delivery</strong>';
      } else {
        msgEl.innerHTML =
          'Add items worth <strong>' + formatMoney(state.needed) +
          '</strong> more for lower delivery';
      }
    }

    // ── Fill bar ──────────────────────────────────────────────
    var fillEl = wrapper.querySelector('.cdp-fill');
    if (fillEl) {
      fillEl.style.width = fillPct + '%';
    }

    // ── Progressbar aria ──────────────────────────────────────
    var trackEl = wrapper.querySelector('.cdp-track');
    if (trackEl) {
      trackEl.setAttribute('aria-valuenow', Math.round(fillPct));
    }

    // ── Tick filled states ────────────────────────────────────
    // Tick i fills when segment >= (i + 1)
    var ticks = wrapper.querySelectorAll('.cdp-tick');
    ticks.forEach(function (tick, i) {
      tick.classList.toggle('cdp-tick--filled', state.segment >= i + 1);
    });

    // Store latest value for reference
    wrapper.dataset.cartCents = totalCents;
  }

  // ── Fetch full cart and update bar ────────────────────────────
  // Extracted as a named function so both fetch and XHR interceptors
  // can call it without duplicating logic.
  function refreshFromCart(nativeFetch) {
    nativeFetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (typeof cart.total_price === 'number') {
          updateBar(cart.total_price, cart.item_count);
        }
      })
      .catch(function () {});
  }

  // ── Cart API endpoints to watch ───────────────────────────────
  var CART_ENDPOINTS = [
    '/cart/add.js',
    '/cart/change.js',
    '/cart/update.js',
    '/cart.js'
  ];

  function isCartUrl(url) {
    return CART_ENDPOINTS.some(function (ep) { return url.indexOf(ep) !== -1; });
  }

  // ── Intercept window.fetch ────────────────────────────────────
  var nativeFetch = window.fetch;

  window.fetch = async function () {
    var response = await nativeFetch.apply(this, arguments);

    var url = typeof arguments[0] === 'string'
      ? arguments[0]
      : (arguments[0] && arguments[0].url) || '';

    if (isCartUrl(url)) {
      var clone = response.clone();
      clone.json().then(function (data) {

        if (url.indexOf('/cart/add.js') !== -1) {
          // /cart/add.js returns the added LINE ITEM, not the full cart.
          // Its total_price is the item subtotal, never the cart total.
          // Always re-fetch /cart.js to get the real cart total.
          refreshFromCart(nativeFetch);

        } else if (typeof data.total_price === 'number') {
          // /cart.js, /cart/change.js, /cart/update.js all return the full cart.
          updateBar(data.total_price, data.item_count);
        }

      }).catch(function () {});
    }

    return response;
  };

  // ── Intercept XMLHttpRequest (some older Shopify theme code) ──
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._cdpUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var url = this._cdpUrl || '';
    if (isCartUrl(url)) {
      this.addEventListener('load', function () {
        try {
          var data = JSON.parse(this.responseText);
          if (url.indexOf('/cart/add.js') !== -1) {
            refreshFromCart(nativeFetch);
          } else if (typeof data.total_price === 'number') {
            updateBar(data.total_price, data.item_count);
          }
        } catch (e) {}
      });
    }
    return nativeSend.apply(this, arguments);
  };

  // ── Shopify / Crave theme custom events ───────────────────────
  ['cart:refresh', 'cart:updated', 'cart-drawer:open'].forEach(function (name) {
    document.addEventListener(name, function (e) {
      var detail = (e && e.detail) || {};
      var cart   = detail.cart || detail;
      if (cart && typeof cart.total_price === 'number') {
        updateBar(cart.total_price, cart.item_count);
      } else {
        refreshFromCart(nativeFetch);
      }
    });
  });

})();


